import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Bar } from "react-chartjs-2";
import { Chart, Legend, Title, Tooltip, Colors } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import annotationPlugin from "chartjs-plugin-annotation";
import deferred from "chartjs-plugin-deferred";
import bulb from "../assets/icons/bulb.svg";
import { Data, Options } from "../types";
import { getAnnotation1, getLabel } from "../utils";

Chart.register(
  ChartDataLabels,
  annotationPlugin,
  deferred,
  Title,
  Tooltip,
  Legend,
  Colors,
);

const BarChart = () => {
  const { t } = useTranslation();
  const [innerWidth, setInnerWidth] = useState(window.innerWidth);
  const imageSize = innerWidth <= 650 ? 36 : 80;
  const thirdData =
    innerWidth <= 650
      ? ["Hydro", "Low-Carbon", "Aluminium", "4,1"]
      : ["Hydro Low-Carbon", "Aluminium", "4,1"];

  useEffect(() => {
    const handleResize = () => {
      setInnerWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const data: Data = {
    labels: [
      [`${t("Primär-Aluminium")}`, `${t("produziert")}`, `${t("in China")}`],
      [`${t("Primär-Aluminium")}`, `${t("produziert")}`, `${t("in Europa")}`],
      thirdData,
    ],
    datasets: [
      {
        categoryPercentage: 0.6,
        data: [16, 6.7, 4.1],
        backgroundColor: [
          "rgba(24, 45, 77, 1)",
          "rgba(29, 67, 118, 1)",
          "rgba(47, 88, 156, 1)",
        ],
      },
    ],
  };

  const options: Options = {
    responsive: true,
    animation: {
      duration: 1500,
    },
    scales: {
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },
      },
      y: {
        display: false,
        grid: {
          display: false,
          drawBorder: false,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      deferred: {
        xOffset: 150,
        yOffset: "50%",
        delay: 1000,
      },
      datalabels: {
        display: true,
        color: "transparent",
        align: "start",
        anchor: "end",
        offset: 1,
        padding: {
          top: innerWidth <= 640 ? 0 : 20,
          right: 0,
        },
        formatter: function (value: number) {
          return value + " kg";
        },
        labels: {
          padding: { top: 1 },
          title: {
            color: "white",
            font: {
              weight: "bold",
              size: 16,
            },
          },
        },
      },
      annotation: {
        annotations: {
          annotation1: getAnnotation1(imageSize, innerWidth),
          label1: getLabel(innerWidth),
        },
      },
      tooltip: {
        enabled: false,
      },
    },
  };

  return (
    <div className="mt-20 max-w-screen-lg items-center mx-8 lg:flex lg:gap-x-10 lg:mt-14 lg:mx-auto">
      <div className="shrink-0">
        <div className="relative lg:w-[749px]">
          <div className="flex flex-col md:items-center">
            <div className="h-auto sm:w-[600px] lg:ml-auto lg:mr-11">
              <Bar data={data} options={options} type={undefined} />
            </div>
          </div>
        </div>
      </div>
      <div className="flex gap-x-4 mt-10 mb-8 lg:flex-col lg:mb-0 lg:mt-16">
        <div className="w-12 h-12 shrink-0">
          <img src={bulb} alt="buld" className="w-full h-full " />
        </div>
        <div>
          <Trans>
            Durch die Verwendung von Reduxa können wir jährlich 7.658 Tonnen CO2
            einsparen.
          </Trans>
          <p className="font-Roboto font-medium mt-2">Quelle: IAI/EAA/Hydro</p>
        </div>
      </div>
    </div>
  );
};

export default BarChart;
